import * as THREE from 'three';
import { isEditableTarget } from './shortcuts.js';

export const FLY_DEADZONE_PX = 5;
export const FLY_KEYS = new Set(['w', 'a', 's', 'd', 'q', 'e']);
export const FLY_SPEED_MIN = 0.1;
export const FLY_SPEED_MAX = 20;
export const FLY_SPEED_STEP = 0.1;
export const FLY_SPEED_STEP_COARSE = 0.5;
export const FLY_BASE_SPEED = 6;
export const FLY_SHIFT_BOOST = 2.5;
export const FLY_LOOK_SENSITIVITY = 0.005;
export const FLY_HUD_HIDE_MS = 800;

const WORLD_UP = new THREE.Vector3(0, 1, 0);
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _euler = new THREE.Euler(0, 0, 0, 'YXZ');

/** Remap OrbitControls to MMB orbit / Shift+MMB pan / no LMB or RMB orbit-pan. */
export function configureOrbitMouseButtons(controls) {
  controls.mouseButtons = {
    LEFT: -1,
    MIDDLE: THREE.MOUSE.ROTATE,
    RIGHT: -1,
  };
}

export function isFlyKey(event) {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  return FLY_KEYS.has(String(event.key ?? '').toLowerCase());
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function formatSpeed(speed) {
  const rounded = Math.round(speed * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
}

function createHud(root) {
  if (!root || typeof globalThis.document === 'undefined') return null;
  const element = globalThis.document.createElement('div');
  element.className = 'fly-speed-hud';
  element.hidden = true;
  element.setAttribute('role', 'status');
  element.setAttribute('aria-live', 'polite');
  root.append(element);
  return element;
}

/**
 * Viewport navigation: MMB orbit (via OrbitControls remap), RMB pending/FlyLook,
 * WASD/QE fly, wheel speed. A still-RMB click (no deadzone, no fly key) fires
 * onStillClick for the context menu; FlyLook release never does.
 */
export function createViewportNavigation({
  viewport,
  keyTarget = globalThis,
  hud,
  hudRoot,
  now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
} = {}) {
  const camera = viewport.camera;
  const controls = viewport.controls;
  const canvas = viewport.renderer.domElement;
  configureOrbitMouseButtons(controls);

  const hudElement = hud === undefined
    ? createHud(hudRoot ?? canvas.parentElement)
    : hud;

  let mode = 'idle';
  let pending = null;
  let lookDistance = 1;
  let flySpeed = 1;
  let lastTime = 0;
  let lastPointer = null;
  let savedControlsEnabled = true;
  let hudHideTimer = 0;
  let suppressClick = false;
  const flyEndListeners = [];
  const stillClickListeners = [];
  const keys = { w: false, a: false, s: false, d: false, q: false, e: false, shift: false };

  function isRmbHeld() {
    return mode === 'pending' || mode === 'fly';
  }

  function isFlyLook() {
    return mode === 'fly';
  }

  function resetKeys() {
    keys.w = keys.a = keys.s = keys.d = keys.q = keys.e = keys.shift = false;
  }

  function pose() {
    return viewport.getCameraPose?.() ?? {
      position: camera.position.toArray(),
      target: controls.target.toArray(),
    };
  }

  function showSpeedHud() {
    if (!hudElement) return;
    hudElement.hidden = false;
    hudElement.dataset.open = 'true';
    hudElement.textContent = `Fly speed ${formatSpeed(flySpeed)}×`;
    if (typeof globalThis.clearTimeout === 'function') {
      globalThis.clearTimeout(hudHideTimer);
      hudHideTimer = globalThis.setTimeout(() => {
        hudElement.hidden = true;
        hudElement.dataset.open = 'false';
      }, FLY_HUD_HIDE_MS);
    }
  }

  function enterFly() {
    if (mode === 'fly') return;
    mode = 'fly';
    pending = pending ?? { pointerId: -1, x: 0, y: 0 };
    savedControlsEnabled = controls.enabled;
    controls.enabled = false;
    // OrbitControls.update() ignores `enabled` and would keep applying
    // leftover orbit damping on top of fly-look. Absorb that inertia once
    // here; the render loop skips update() while controls are disabled.
    if (typeof controls.update === 'function') {
      const damping = controls.enableDamping;
      controls.enableDamping = false;
      controls.update();
      controls.enableDamping = damping;
    }
    lookDistance = Math.max(camera.position.distanceTo(controls.target), 0.01);
    lastTime = now();
    lastPointer = pending ? { x: pending.x, y: pending.y } : null;
    canvas.style.cursor = 'none';
    canvas.requestPointerLock?.();
  }

  function exitFly({ report = true } = {}) {
    const wasFlying = mode === 'fly';
    mode = 'idle';
    pending = null;
    lastPointer = null;
    resetKeys();
    controls.enabled = savedControlsEnabled;
    canvas.style.cursor = '';
    globalThis.document?.exitPointerLock?.();
    if (wasFlying && report) {
      const next = pose();
      for (const listener of flyEndListeners) listener(next);
    }
  }

  /** Abort pending RMB or FlyLook without treating it as a still-click. */
  function abortHeldRmb() {
    if (mode === 'fly') {
      exitFly();
      return true;
    }
    if (mode === 'pending') {
      mode = 'idle';
      pending = null;
      lastPointer = null;
      resetKeys();
      return true;
    }
    return false;
  }

  function applyLook(dx, dy) {
    if (dx === 0 && dy === 0) return;
    _euler.setFromQuaternion(camera.quaternion);
    _euler.y -= dx * FLY_LOOK_SENSITIVITY;
    _euler.x -= dy * FLY_LOOK_SENSITIVITY;
    const limit = Math.PI / 2 - 0.01;
    _euler.x = clamp(_euler.x, -limit, limit);
    _euler.z = 0;
    camera.quaternion.setFromEuler(_euler);
  }

  function updateLookTarget() {
    camera.getWorldDirection(_forward);
    controls.target.copy(camera.position).addScaledVector(_forward, lookDistance);
  }

  function startPending(event) {
    if (event.button !== 2) return;
    if (mode !== 'idle') return;
    event.preventDefault();
    mode = 'pending';
    pending = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    lastPointer = { x: event.clientX, y: event.clientY };
    resetKeys();
  }

  function lookDelta(event) {
    // Pointer lock reports relative motion on movementX/Y and freezes clientX.
    // Without a lock (denied, pending, or tests), movementX is often 0 even
    // while the cursor is moving — use client deltas in that case.
    if (globalThis.document?.pointerLockElement === canvas) {
      return { dx: event.movementX || 0, dy: event.movementY || 0 };
    }
    return {
      dx: event.clientX - (lastPointer?.x ?? event.clientX),
      dy: event.clientY - (lastPointer?.y ?? event.clientY),
    };
  }

  function movePending(event) {
    if (!pending || event.pointerId !== pending.pointerId) return;
    if (mode === 'pending') {
      const distance = Math.hypot(event.clientX - pending.x, event.clientY - pending.y);
      if (distance > FLY_DEADZONE_PX) enterFly();
    }
    if (mode !== 'fly') return;
    const { dx, dy } = lookDelta(event);
    lastPointer = { x: event.clientX, y: event.clientY };
    applyLook(dx, dy);
    updateLookTarget();
  }

  function finishPending(event) {
    if (!pending || event.pointerId !== pending.pointerId) return;
    if (mode === 'fly') {
      exitFly();
      return;
    }
    const click = { x: pending.x, y: pending.y };
    const stillClick = event.type === 'pointerup';
    mode = 'idle';
    pending = null;
    resetKeys();
    if (!stillClick) return;
    // A still-RMB can be followed by a synthesized `click` on this canvas
    // (button 0 by default). Object/mesh LMB handlers live on `click` and
    // would then select, clear, or frame as if this were a left click.
    suppressClick = true;
    queueMicrotask(() => { suppressClick = false; });
    for (const listener of stillClickListeners) listener(click);
  }

  function onSuppressedClick(event) {
    if (!suppressClick) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function onKeyDown(event) {
    if (isEditableTarget(event.target)) return;
    if (event.key === 'Shift') keys.shift = true;
    if (!isRmbHeld()) return;
    if (!isFlyKey(event)) return;
    if (mode === 'pending') enterFly();
    keys[event.key.toLowerCase()] = true;
    event.preventDefault();
  }

  function onKeyUp(event) {
    if (event.key === 'Shift') keys.shift = false;
    const key = String(event.key ?? '').toLowerCase();
    if (key in keys && key !== 'shift') keys[key] = false;
  }

  function onWheel(event) {
    if (mode !== 'fly') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const step = event.shiftKey ? FLY_SPEED_STEP_COARSE : FLY_SPEED_STEP;
    flySpeed = clamp(flySpeed + (event.deltaY < 0 ? step : -step), FLY_SPEED_MIN, FLY_SPEED_MAX);
    showSpeedHud();
  }

  function update(timestamp = now()) {
    if (mode !== 'fly') {
      lastTime = timestamp;
      return;
    }
    const dt = Math.min(Math.max((timestamp - lastTime) / 1000, 0), 0.05);
    lastTime = timestamp;
    if (dt === 0) return;

    const distance = FLY_BASE_SPEED * flySpeed * (keys.shift ? FLY_SHIFT_BOOST : 1) * dt;
    camera.getWorldDirection(_forward);
    _forward.y = 0;
    if (_forward.lengthSq() < 1e-8) _forward.set(0, 0, -1);
    else _forward.normalize();
    _right.crossVectors(_forward, WORLD_UP);
    if (_right.lengthSq() < 1e-8) _right.set(1, 0, 0);
    else _right.normalize();

    if (keys.w) camera.position.addScaledVector(_forward, distance);
    if (keys.s) camera.position.addScaledVector(_forward, -distance);
    if (keys.a) camera.position.addScaledVector(_right, -distance);
    if (keys.d) camera.position.addScaledVector(_right, distance);
    if (keys.e) camera.position.y += distance;
    if (keys.q) camera.position.y -= distance;
    updateLookTarget();
  }

  function onContextMenu(event) {
    event.preventDefault();
  }

  canvas.addEventListener('pointerdown', startPending);
  canvas.addEventListener('pointermove', movePending);
  canvas.addEventListener('pointerup', finishPending);
  canvas.addEventListener('pointercancel', finishPending);
  canvas.addEventListener('click', onSuppressedClick, true);
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('wheel', onWheel, { capture: true, passive: false });
  keyTarget.addEventListener('keydown', onKeyDown);
  keyTarget.addEventListener('keyup', onKeyUp);

  return {
    isRmbHeld,
    isFlyLook,
    abortHeldRmb,
    getFlySpeed: () => flySpeed,
    getMode: () => mode,
    update,
    onFlyEnd(callback) {
      flyEndListeners.push(callback);
    },
    onStillClick(callback) {
      stillClickListeners.push(callback);
    },
    destroy() {
      if (mode === 'fly') exitFly({ report: false });
      mode = 'idle';
      pending = null;
      if (typeof globalThis.clearTimeout === 'function') globalThis.clearTimeout(hudHideTimer);
      hudElement?.remove?.();
      canvas.removeEventListener('pointerdown', startPending);
      canvas.removeEventListener('pointermove', movePending);
      canvas.removeEventListener('pointerup', finishPending);
      canvas.removeEventListener('pointercancel', finishPending);
      canvas.removeEventListener('click', onSuppressedClick, true);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('wheel', onWheel, { capture: true });
      keyTarget.removeEventListener('keydown', onKeyDown);
      keyTarget.removeEventListener('keyup', onKeyUp);
    },
  };
}
